// tslint:disable:no-trailing-whitespace
// tslint:disable:member-ordering
import { BaseEntity } from '../base-entity';
import { Account } from './account';
import { AccountProcurementCard } from './account-procurement-card';
import { AccountPurchaseOrder } from './account-purchase-order';
import { ActiveStatus } from './active-status';
import { ApprovalTree } from './approval-tree';
import { CollectionScope } from './collection-scope';
import { JobOrder } from './job-order';
import { ProgramAccountIssuanceMap } from './program-account-issuance-map';
import { ProgramAllowance } from './program-allowance';
import { ProgramAllowanceRemainderRule } from './program-allowance-remainder-rule';
import { ProgramBillingConfig } from './program-billing-config';
import { ProgramBudget } from './program-budget';
import { ProgramIssuance } from './program-issuance';
import { ProgramKit } from './program-kit';
import { ProgramKitIssuance } from './program-kit-issuance';
import { ProgramPcardType } from './program-pcard-type';
import { ProgramProductCategoryTag } from './program-product-category-tag';
import { ProgramProductTypeConfig } from './program-product-type-config';
import { ProgramPurchaseOrderType } from './program-purchase-order-type';
import { ProgramRapidTemplate } from './program-rapid-template';
import { ProgramReview } from './program-review';
import { ProgramStartDateType } from './program-start-date-type';
import { ProgramSupplier } from './program-supplier';
import { ProgramType } from './program-type';
import { ProgramUserGroupExclusion } from './program-user-group-exclusion';
import { ProgramView } from './program-view';
import { ProgramViolationRule } from './program-violation-rule';
import { ShipFrequency } from './ship-frequency';
import { ActiveStatusEnum } from './enums';
import { CollectionScopeEnum } from './enums';
import { ShipFrequencyEnum } from './enums';
import { ProgramViolationRuleEnum } from './enums';
import { ProgramAllowanceRemainderRuleEnum } from './enums';
import { ProgramPcardTypeEnum } from './enums';
import { ProgramPurchaseOrderTypeEnum } from './enums';
import { ProgramStartDateTypeEnum } from './enums';
import { ProgramTypeEnum } from './enums';

/// <code-import> Place custom imports between <code-import> tags
import { EntityPropertyAnnotation, EntityTypeAnnotation } from '../entity-type-annotations';
import { DateFns, UtilFns } from '@utils';
import { AccountUser } from './account-user';
import { ProxAgFns } from '@core';
/// </code-import>

/// <module-code> Place module level code between <module-code> tags

/// </module-code>

export class Program extends BaseEntity  {

  /// <code> Place custom code between <code> tags
  // static getEntityTypeAnnotation(): EntityTypeAnnotation {

  //   const propAnnotations = [
  //     // TODO: need a validator that requires a start and end date based on the programStartDateType
  //   ];

  //   return new EntityTypeAnnotation({
  //     validators: [  ],
  //     propertyAnnotations: propAnnotations
  //   });

  // }

  // used as a temporary storage area when building new jobOrders.
  _previousProgramId!: string;

  hasStatusChanged() {
    const ea = this.entityAspect;
    if (!ea) return false;
    if (ea.entityState.isUnchanged()) return false;
    return this.activeStatusId != this.getOriginalStatusId();
  }

  getOriginalStatusId() {
    return this.entityAspect?.originalValues[this.activeStatusId] ?? this.activeStatusId;
  }

  hasExplicitEndDate() {
    return (
      this.programStartDateTypeId == ProgramStartDateTypeEnum.FixedStartAndEndDate ||
      this.programStartDateTypeId == ProgramStartDateTypeEnum.VariableStartFixedEndDate
    );
  }

  hasExplicitStartDate() {
    return this.programStartDateTypeId == ProgramStartDateTypeEnum.FixedStartAndEndDate;
  }

  /**
   * True when this program is still live and relevant: not Inactive, not cancelled, not expired.
   *
   * NOT the same as "Active". Administrators retire a program cycle by renaming it XX<name> and
   * parking it on Hold — never setting it Inactive — so expired cycles sit around for years
   * looking live. Counting non-Inactive programs overstates reality badly: on the shared-tree
   * analysis it reported 70 conflicts where the true number was 9.
   *
   * A VariableStartAndEndDate program is always current here. It has no program-level end date to
   * judge (it expires per user from joinDate + durationDays), so the only honest answer at program
   * scope is "cannot rule it out".
   *
   * Mirrors Program.IsCurrent(DateTime) on the server (Proximity.Model/Program.cs), which is the
   * canonical definition. Keep them in step.
   *
   * Answers "is this program still live in the broad sense" — is its tree spoken for, will it
   * still need a dedicated tree. For "can two programs collide over one tree's per-group purchase
   * orders", use isAcceptingNewOrders() instead.
   */
  isCurrent(today?: Date) {
    const asOf = today ?? DateFns.today();
    if (this.activeStatusId === ActiveStatusEnum.Inactive) return false;
    if (this.cancellationDate != null && this.cancellationDate <= asOf) return false;
    if (this.hasExplicitEndDate() && this.endDate != null && this.endDate < asOf) return false;
    return true;
  }

  /**
   * Whether this program can accept NEW orders. The predicate that governs Organization Tree
   * exclusivity, and the one the designation guard uses.
   *
   * Per-PUG purchase orders resolve at PLACEMENT, from (ProgramUserGroup x Tree), and an
   * already-placed order keeps the purchase order stamped on its JobOrder. So a program that
   * cannot accept a new order cannot mis-route one, however live it otherwise looks. Hold and
   * Phaseout both "continue to allow shipments on existing orders but will not accept new orders"
   * — the app's own wording to the administrator.
   *
   * Note this is NOT just activeStatusId === Active: an Active program whose end date has passed
   * accepts nothing either, and in production plenty of Active programs are expired.
   *
   * Mirrors Program.IsAcceptingNewOrders(DateTime) on the server. Keep them in step.
   */
  isAcceptingNewOrders(today?: Date) {
    return this.activeStatusId === ActiveStatusEnum.Active && this.isCurrent(today);
  }

  // Jay: is this code duplicated elsewhere?
  // isOpen(au: AccountUser) {
  //   // get the join date;

  //   if (this.programStartDateTypeId === ProgramStartDateTypeEnum.FixedStartAndEndDate || this.programStartDateTypeId === ProgramStartDateTypeEnum.VariableStartFixedEndDate) {
  //     return (DateFns.today() <= <Date>this.endDate)
  //   }

  //   if (this.programStartDateTypeId === ProgramStartDateTypeEnum.VariableStartAndEndDate) {
  //     if (!au.hireDate || !this.startAfterAnniversaryNumDays || !this.endsAfterAnniversaryNumDays) return false;
  //     const startDate = DateFns.dateAdd(au.hireDate, this.startAfterAnniversaryNumDays, 'days');
  //     const endDate = DateFns.dateAdd(au.hireDate, this.endsAfterAnniversaryNumDays, 'days');
  //     return (startDate <= DateFns.today() && endDate >= DateFns.today());
  //   }

  //   return false;
  // }

  get isExpired(): boolean {
    if (this.endDate) {
      // Ensure endDate is compared as a Date object (Breeze may return it as a string)
      const endDateObj = this.endDate instanceof Date ? this.endDate : new Date(this.endDate);
      return endDateObj < new Date();
    }
    return false;
  }

  get extProgramName(): string {
    if (this.endDate) {
      // Ensure endDate is compared as a Date object (Breeze may return it as a string)
      const endDateObj = this.endDate instanceof Date ? this.endDate : new Date(this.endDate);
      if (endDateObj < new Date()) {
        return this.name + ' (end date: ' + DateFns.fmtDateShort(this.endDate) + ' EXPIRED)';
      } else {
        return this.name + ' (end date: ' + DateFns.fmtDateShort(this.endDate);
      }
    }
    return this.name;
  }

  get programTypeLongName(): string {
    switch (this.programTypeId) {
      case ProgramTypeEnum.Inventory:
        return 'Managed Inventory';
      case ProgramTypeEnum.Managed:
        return 'Managed Distribution';
      default:
        return this.programType.name;
    }
  }

  getStatusParents() {
    return [this.account];
  }

  getStatusId() {
    return this.activeStatusId;
  }

  getProgramUserGroups() {
    return this.approvalTree?.approvalTreeUserGroups.map((x) => x.programUserGroup) ?? [];
  }
  /// </code>

  // Generated code. Do not place code below this line.
  /** Guid key */
  id!: string;
  /** Guid */
  accountAdminGroupId?: string;
  /** Guid FK account */
  accountId!: string;
  /** Guid FK accountProcurementCard */
  accountProcurementCardId?: string;
  /** Guid FK accountPurchaseOrder */
  accountPurchaseOrderId?: string;
  /** Enum FK activeStatus */
  activeStatusId!: ActiveStatusEnum;
  /** Enum FK allowanceProgramViolationRule */
  allowanceProgramViolationRuleId!: ProgramViolationRuleEnum;
  /** Guid FK approvalTree */
  approvalTreeId?: string;
  /** Int32 */
  availableAfterJoinDateDays!: number;
  /** Boolean */
  canShipHome!: boolean;
  /** DateTime */
  cancellationDate?: Date;
  /** Boolean */
  collectAndPlace!: boolean;
  /** DateTimeOffset */
  collectionReleasedTs?: Date;
  /** Enum FK collectionScope */
  collectionScopeId!: CollectionScopeEnum;
  /** DateTime */
  collectionStartDate?: Date;
  /** String */
  description?: string;
  /** Boolean */
  doAllOrdersRequireApproval!: boolean;
  /** Int32 */
  durationDays?: number;
  /** DateTime */
  earliestPlacementDate?: Date;
  /** DateTime */
  endDate?: Date;
  /** Boolean */
  isPcardRequired!: boolean;
  /** Boolean */
  isPurchaseOrderRequired!: boolean;
  /** Enum FK issuanceProgramViolationRule */
  issuanceProgramViolationRuleId!: ProgramViolationRuleEnum;
  /** String(60) */
  name!: string;
  /** Enum FK programAllowanceRemainderRule */
  programAllowanceRemainderRuleId!: ProgramAllowanceRemainderRuleEnum;
  /** Enum FK programPcardType */
  programPcardTypeId!: ProgramPcardTypeEnum;
  /** Enum FK programPurchaseOrderType */
  programPurchaseOrderTypeId!: ProgramPurchaseOrderTypeEnum;
  /** Enum FK programStartDateType */
  programStartDateTypeId!: ProgramStartDateTypeEnum;
  /** Enum FK programType */
  programTypeId!: ProgramTypeEnum;
  /** Binary */
  rowVersion!: any;
  /** Enum FK shipFrequency */
  shipFrequencyId!: ShipFrequencyEnum;
  /** DateTime */
  startDate?: Date;
  /** FK accountId */
  account!: Account;
  /** FK accountProcurementCardId */
  accountProcurementCard!: AccountProcurementCard;
  /** FK accountPurchaseOrderId */
  accountPurchaseOrder!: AccountPurchaseOrder;
  /** FK activeStatusId */
  activeStatus!: ActiveStatus;
  /** FK allowanceProgramViolationRuleId */
  allowanceProgramViolationRule!: ProgramViolationRule;
  /** FK approvalTreeId */
  approvalTree!: ApprovalTree;
  /** FK collectionScopeId */
  collectionScope!: CollectionScope;
  /** FK issuanceProgramViolationRuleId */
  issuanceProgramViolationRule!: ProgramViolationRule;
  /** Inv FK programId */
  jobOrders!: JobOrder[];
  /** Inv FK programId */
  programAccountIssuanceMaps!: ProgramAccountIssuanceMap[];
  /** FK programAllowanceRemainderRuleId */
  programAllowanceRemainderRule!: ProgramAllowanceRemainderRule;
  /** Inv FK programId */
  programAllowances!: ProgramAllowance[];
  /** Inv FK programId */
  programBillingConfigs!: ProgramBillingConfig[];
  /** Inv FK programId */
  programBudgets!: ProgramBudget[];
  /** Inv FK programId */
  programIssuances!: ProgramIssuance[];
  /** Inv FK programId */
  programKitIssuances!: ProgramKitIssuance[];
  /** Inv FK programId */
  programKits!: ProgramKit[];
  /** FK programPcardTypeId */
  programPcardType!: ProgramPcardType;
  /** Inv FK programId */
  programProductCategoryTags!: ProgramProductCategoryTag[];
  /** Inv FK programId */
  programProductTypeConfigs!: ProgramProductTypeConfig[];
  /** FK programPurchaseOrderTypeId */
  programPurchaseOrderType!: ProgramPurchaseOrderType;
  /** Inv FK programId */
  programRapidTemplates!: ProgramRapidTemplate[];
  /** Inv FK programId */
  programReviews!: ProgramReview[];
  /** FK programStartDateTypeId */
  programStartDateType!: ProgramStartDateType;
  /** Inv FK programId */
  programSuppliers!: ProgramSupplier[];
  /** FK programTypeId */
  programType!: ProgramType;
  /** Inv FK programId */
  programUserGroupExclusions!: ProgramUserGroupExclusion[];
  /** Inv FK programId */
  programViews!: ProgramView[];
  /** FK shipFrequencyId */
  shipFrequency!: ShipFrequency;
}

